import { DialogOpenEvent } from '@theme/dialog';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Shopify's <shopify-accelerated-checkout-cart> custom element self-triggers
 * a large checkout-web download the instant it's connected to the document,
 * regardless of visibility. To keep that off the critical path, the Liquid
 * side (snippets/cart-summary.liquid) renders it inside an inert <template>
 * instead of live in the DOM. This script is responsible for cloning that
 * template's content into place once it's actually needed.
 *
 * Hydration is triggered from three separate signals:
 *   1. Idle time shortly after load, so it's normally already loaded by the
 *      time a shopper opens their cart.
 *   2. A cart drawer or dialog opening, as a guarantee if idle time hasn't
 *      fired yet. BOTH events are needed. Horizon 4.1.3 rewrote the cart
 *      drawer from DialogComponent to <theme-drawer>, so it now dispatches
 *      'theme-drawer:open' and no longer dispatches 'dialog:open' — listening
 *      only for the latter would silently stop covering the cart drawer, with
 *      no error, because @theme/dialog still resolves and still exports the
 *      event class. 'dialog:open' is still needed for quick-add and the other
 *      remaining DialogComponent dialogs.
 *   3. A MutationObserver, for the case where a cart-summary section is
 *      rendered fresh rather than morphed (the server HTML always contains the
 *      inert <template> again, since that's what the Liquid source emits).
 *
 * On signal 3 and morphing: the wrapper in cart-summary.liquid carries
 * data-skip-subtree-update, which makes assets/morph.js bail out of
 * updateChildren for that subtree. That is what stops a cart update (add item,
 * change quantity, apply discount) from replacing the already-hydrated element
 * with the inert template again — morph.js's own
 * SHOPIFY-ACCELERATED-CHECKOUT-CART guard does not help, because it sits in
 * walk() and walk() is only reached when tag names already match. Before that
 * attribute existed this observer was the only thing keeping the buttons alive
 * across cart updates, so it ran hydrate() on every document mutation; it is
 * now a narrow fallback and early-outs unless a template actually appeared.
 */

const TEMPLATE_SELECTOR = 'template[data-accelerated-checkout-template]';

function hydrate() {
  document.querySelectorAll(TEMPLATE_SELECTOR).forEach((template) => {
    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = document.importNode(template.content, true);

    // Scripts cloned via importNode/cloneNode don't execute, so any <script>
    // that came along with the checkout markup (e.g. the checkout-web
    // bootstrap loader) must be recreated to force execution — same
    // workaround already used in assets/morph.js's recreateAppBlockScripts().
    fragment.querySelectorAll('script').forEach((script) => {
      const replacement = document.createElement('script');
      for (const attr of Array.from(script.attributes)) {
        replacement.setAttribute(attr.name, attr.value);
      }
      if (script.textContent) {
        replacement.textContent = script.textContent;
      }
      script.replaceWith(replacement);
    });

    template.replaceWith(fragment);
  });
}

function scheduleIdleHydration() {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(hydrate, { timeout: 2000 });
  } else {
    // Safari has no requestIdleCallback.
    setTimeout(hydrate, 200);
  }
}

/**
 * True if any added node is, or contains, a pending template. Keeps the
 * observer off the critical path: INP is already tight on this theme, and the
 * previous unconditional hydrate() ran a document-wide querySelectorAll on
 * every mutation.
 */
function addedNodesContainTemplate(mutations) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.(TEMPLATE_SELECTOR) || node.querySelector?.(TEMPLATE_SELECTOR)) {
        return true;
      }
    }
  }
  return false;
}

scheduleIdleHydration();

// DialogOpenEvent does not bubble (see assets/dialog.js), so a bubble-phase
// document listener would miss it — capture-phase listening still observes
// it on the way down regardless of the event's bubbling behavior.
document.addEventListener(DialogOpenEvent.eventName, hydrate, { capture: true });

// DrawerOpenEvent does bubble (assets/theme-drawer.js), so this one does not
// need capture — but it costs nothing to be consistent and it also makes the
// listener immune to a future change in that event's bubbling.
document.addEventListener(DrawerOpenEvent.eventName, hydrate, { capture: true });

new MutationObserver((mutations) => {
  if (addedNodesContainTemplate(mutations)) hydrate();
}).observe(document.body, { childList: true, subtree: true });
